import { HanamiElement, createShadow, define, prefersReducedMotion } from '../core/base';
import { t } from '../core/strings';

const css = `:host{--hn-carousel-per-view:1;--hn-carousel-gap:var(--hn-space-4);display:block;position:relative}*,*::before,*::after{box-sizing:border-box}.viewport{display:flex;gap:var(--hn-carousel-gap);overflow-x:auto;overscroll-behavior-x:contain;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;border-radius:var(--hn-radius-xl);outline:none}.viewport::-webkit-scrollbar{display:none}.viewport:focus-visible{box-shadow:var(--hn-focus-ring)}::slotted(*){flex:0 0 calc((100% - (var(--hn-carousel-per-view) - 1) * var(--hn-carousel-gap)) / var(--hn-carousel-per-view));min-width:0;scroll-snap-align:start}.nav{position:absolute;top:50%;z-index:1;display:grid;place-items:center;width:2.75rem;height:2.75rem;padding:0;border:1px solid var(--hn-color-border);border-radius:50%;background:color-mix(in srgb,var(--hn-color-surface) 88%,transparent);box-shadow:var(--hn-shadow-md);color:var(--hn-color-text);cursor:pointer;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);transform:translateY(-50%);transition:opacity var(--hn-duration-normal),transform var(--hn-duration-normal) var(--hn-ease-bloom),background-color var(--hn-duration-fast),color var(--hn-duration-fast)}.nav:hover{background:var(--hn-color-primary);border-color:transparent;color:var(--hn-color-on-primary);transform:translateY(-50%) scale(1.06)}.nav:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}.nav[disabled]{opacity:0;pointer-events:none}.nav svg{width:1.25rem;height:1.25rem}.prev{left:var(--hn-space-3)}.next{right:var(--hn-space-3)}:host([no-arrows]) .nav{display:none}.footer{display:flex;align-items:center;justify-content:center;gap:var(--hn-space-3);margin-top:var(--hn-space-3)}.dots{display:flex;flex-wrap:wrap;justify-content:center;gap:0.375rem}:host([no-dots]) .dots{display:none}.dot{width:0.625rem;height:0.625rem;padding:0;border:0;border-radius:var(--hn-radius-full);background:var(--hn-color-border-strong);cursor:pointer;transition:width var(--hn-duration-slow) var(--hn-ease-bloom),background-color var(--hn-duration-normal)}.dot:hover{background:var(--hn-color-primary-border)}.dot[aria-current="true"]{width:1.75rem;background:var(--hn-color-primary)}.dot:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}.play{display:none;place-items:center;width:1.75rem;height:1.75rem;padding:0;border:0;border-radius:50%;background:transparent;color:var(--hn-color-text-muted);cursor:pointer}:host([autoplay]) .play{display:grid}.play:hover{background:var(--hn-color-hover);color:var(--hn-color-text)}.play:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}.play svg{width:1rem;height:1rem}.play .icon-play{display:none}.play[aria-pressed="true"] .icon-play{display:block}.play[aria-pressed="true"] .icon-pause{display:none}.live{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}@media (prefers-reduced-motion:reduce){.viewport{scroll-behavior:auto}}`;

const chevron = (d: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;

const html = /* html */ `
  <div class="viewport" part="viewport" tabindex="0"><slot></slot></div>
  <button class="nav prev" part="prev" type="button">${chevron('m15 18-6-6 6-6')}</button>
  <button class="nav next" part="next" type="button">${chevron('m9 18 6-6-6-6')}</button>
  <div class="footer" part="footer">
    <button class="play" part="play" type="button" aria-pressed="false">
      <svg class="icon-pause" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
      <svg class="icon-play" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"/></svg>
    </button>
    <div class="dots" part="dots"></div>
  </div>
  <div class="live" aria-live="polite"></div>
`;

export interface CarouselChangeDetail {
  index: number;
}

/**
 * Carrousel à défilement natif (scroll-snap), flèches, pastilles et lecture automatique.
 *
 * Attributs : loop, autoplay (ms, défaut 5000 si vide), no-arrows, no-dots, label.
 * Variables CSS : --hn-carousel-per-view, --hn-carousel-gap.
 * Événement : hn-slide-change (detail.index).
 */
export class HnCarousel extends HanamiElement {
  static readonly tagName = 'hn-carousel';
  static get observedAttributes() {
    return ['autoplay', 'label'];
  }

  private viewport!: HTMLElement;
  private dots!: HTMLElement;
  private prevButton!: HTMLButtonElement;
  private nextButton!: HTMLButtonElement;
  private playButton!: HTMLButtonElement;
  private liveRegion!: HTMLElement;
  private current = 0;
  private timer?: ReturnType<typeof setInterval>;
  private paused = false;
  private userPaused = false;
  private scrollFrame = 0;
  private resizeObserver?: ResizeObserver;

  get slides(): HTMLElement[] {
    return Array.from(this.children).filter((el): el is HTMLElement => el instanceof HTMLElement && !el.slot);
  }

  /** Index de la diapositive active. */
  get index(): number {
    return this.current;
  }

  private get positions(): number {
    const perView = Math.max(1, Math.round(Number(getComputedStyle(this).getPropertyValue('--hn-carousel-per-view')) || 1));
    return Math.max(1, this.slides.length - perView + 1);
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.viewport = root.querySelector('.viewport')!;
      this.dots = root.querySelector('.dots')!;
      this.prevButton = root.querySelector('.prev')!;
      this.nextButton = root.querySelector('.next')!;
      this.playButton = root.querySelector('.play')!;
      this.liveRegion = root.querySelector('.live')!;
      this.prevButton.setAttribute('aria-label', t('previousSlide'));
      this.nextButton.setAttribute('aria-label', t('nextSlide'));
      this.prevButton.addEventListener('click', () => this.prev());
      this.nextButton.addEventListener('click', () => this.next());
      this.playButton.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncAutoplay();
      });
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);
      root.querySelector('slot')!.addEventListener('slotchange', () => this.renderDots());
    }
    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', t('carousel'));
    this.syncLabel();
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.renderDots());
      this.resizeObserver.observe(this);
    }
    this.renderDots();
    this.syncAutoplay();
  }

  disconnectedCallback(): void {
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.resizeObserver?.disconnect();
    clearInterval(this.timer);
  }

  attributeChangedCallback(name: string): void {
    if (!this.viewport) return;
    if (name === 'autoplay') this.syncAutoplay();
    if (name === 'label') this.syncLabel();
  }

  /** Va à la diapositive `index` (0 = première). */
  goTo(index: number, { smooth = true } = {}): void {
    const max = this.positions - 1;
    const loop = this.getBool('loop');
    let target = index;
    if (target > max) target = loop ? 0 : max;
    if (target < 0) target = loop ? max : 0;
    const slide = this.slides[target];
    if (!slide) return;
    const left = slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft;
    this.viewport.scrollTo({ left, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
    this.setCurrent(target);
  }

  next(): void {
    this.goTo(this.current + 1);
  }

  prev(): void {
    this.goTo(this.current - 1);
  }

  private syncLabel(): void {
    this.setAttribute('aria-label', this.getAttribute('label') || t('carousel'));
  }

  private setCurrent(index: number): void {
    const changed = index !== this.current;
    this.current = index;
    this.dots.querySelectorAll('button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    const loop = this.getBool('loop');
    this.prevButton.disabled = !loop && index <= 0;
    this.nextButton.disabled = !loop && index >= this.positions - 1;
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', t('slideOf', { n: i + 1, total: this.slides.length }));
    });
    if (changed) {
      if (!this.timer) this.liveRegion.textContent = t('slideOf', { n: index + 1, total: this.slides.length });
      this.emit<CarouselChangeDetail>('hn-slide-change', { index });
    }
  }

  private renderDots(): void {
    const count = this.positions;
    if (this.dots.childElementCount !== count) {
      this.dots.replaceChildren(
        ...Array.from({ length: count }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'dot';
          dot.setAttribute('part', 'dot');
          dot.setAttribute('aria-label', t('goToSlide', { n: i + 1 }));
          dot.addEventListener('click', () => this.goTo(i));
          return dot;
        }),
      );
    }
    this.dots.hidden = count < 2;
    this.setCurrent(Math.min(this.current, count - 1));
  }

  private onScroll = (): void => {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const origin = this.viewport.getBoundingClientRect().left;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.slice(0, this.positions).forEach((slide, i) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - origin);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      if (best !== this.current) this.setCurrent(best);
    });
  };

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.target !== this.viewport) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  };

  private get interval(): number {
    const value = this.getAttribute('autoplay');
    if (value === null || value === 'false') return 0;
    const ms = Number(value);
    return Number.isFinite(ms) && ms >= 1500 ? ms : 5000;
  }

  private syncAutoplay(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    const interval = this.interval;
    this.playButton.setAttribute('aria-pressed', String(this.userPaused));
    this.playButton.setAttribute('aria-label', this.userPaused ? t('play') : t('pause'));
    if (!interval || this.userPaused || this.paused || prefersReducedMotion()) return;
    this.timer = setInterval(() => {
      const atEnd = this.current >= this.positions - 1;
      this.goTo(atEnd ? 0 : this.current + 1);
    }, interval);
  }

  private pause = (): void => {
    this.paused = true;
    this.syncAutoplay();
  };

  private resume = (): void => {
    if (this.matches(':focus-within')) return;
    this.paused = false;
    this.syncAutoplay();
  };

  private onFocusOut = (event: FocusEvent): void => {
    if (!this.contains(event.relatedTarget as Node) && !this.shadowRoot!.contains(event.relatedTarget as Node)) {
      this.paused = false;
      this.syncAutoplay();
    }
  };

  private onVisibility = (): void => {
    this.paused = document.hidden;
    this.syncAutoplay();
  };
}

define(HnCarousel.tagName, HnCarousel);
